import React, { useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import clsx from "clsx";
import { queryFailedJobs, processJobRetries, deleteJobRecords, markJobsAsResolved } from './api_utils';
import { renderTable } from "./RenderTable";
import { LogsViewer } from "./LogsViewer";

const FailedJobsTable = () => {
  const [selectedFunctions, setSelectedFunctions] = useState(["All"]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [resolvedFilter, setResolvedFilter] = useState("Unresolved");
  const [activeTab, setActiveTab] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedJobIds, setSelectedJobIds] = useState(new Set());
  const [isRetrying, setIsRetrying] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMarkingResolved, setIsMarkingResolved] = useState(false);
  const [logJob, setLogJob] = useState(null);

  // Pagination state
  const [lastKey, setLastKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pageSize] = useState(50); // Items per page
  const [totalLoaded, setTotalLoaded] = useState(0);

  // Handle viewing logs for resolved jobs
  const handleViewLogs = (job) => {
    setLogJob(job);
  };

  const handleCloseLogs = () => {
    setLogJob(null);
  };

  // Handle job selection
  const handleJobSelect = (jobId, isChecked) => {
    const newSelected = new Set(selectedJobIds);
    if (isChecked) {
      newSelected.add(jobId);
    } else {
      newSelected.delete(jobId);
    }
    setSelectedJobIds(newSelected);
  };

  // Handle select all for current tab
  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      const allJobIds = activeTabData.map(job => job.job_id);
      setSelectedJobIds(new Set([...selectedJobIds, ...allJobIds]));
    } else {
      const currentTabJobIds = new Set(activeTabData.map(job => job.job_id));
      const newSelected = new Set([...selectedJobIds].filter(id => !currentTabJobIds.has(id)));
      setSelectedJobIds(newSelected);
    }
  };

  // Handle delete selected jobs
  const handleDeleteJobs = async () => {
    if (selectedJobIds.size === 0) {
      alert('Please select jobs to delete');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedJobIds.size} job record(s)? This action cannot be undone.`
    );

    if (!confirmDelete) {
      return;
    }

    setIsDeleting(true);
    try {
      const selectedJobs = data.filter(job => selectedJobIds.has(job.job_id));
      const results = await deleteJobRecords(selectedJobs);

      let message = `🗑️ Delete Process Completed:\n\n`;
      message += `✅ Successfully deleted: ${results.successful.length} record(s)\n`;
      message += `❌ Failed to delete: ${results.failed.length} record(s)\n\n`;

      if (results.failed.length > 0) {
        message += `❌ Failed Deletions:\n`;
        results.failed.forEach(f => {
          message += `• ${f.jobId}: ${f.error}\n`;
        });
      }

      alert(message);
      setSelectedJobIds(new Set());

      if (results.successful.length > 0) {
        const deletedJobIds = new Set(results.successful.map(s => s.jobId));
        setData(prevData => prevData.filter(job => !deletedJobIds.has(job.job_id)));
        setTotalLoaded(prev => prev - results.successful.length);
      }

    } catch (error) {
      console.error('Error in deletion process:', error);
      alert('Error in deletion process: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle retry selected jobs
  const handleRetryJobs = async () => {
    if (selectedJobIds.size === 0) {
      alert('Please select jobs to retry');
      return;
    }

    setIsRetrying(true);
    try {
      const selectedJobs = data.filter(job => selectedJobIds.has(job.job_id));
      const results = await processJobRetries(selectedJobs);

      let message = `🔄 Retry Process Completed:\n\n`;
      message += `📡 Lambda Invocations:\n`;
      message += `✅ Successful: ${results.lambda.successful.length}\n`;
      message += `❌ Failed: ${results.lambda.failed.length}\n\n`;

      if (results.lambda.successful.length > 0) {
        message += `💾 Database Updates:\n`;
        message += `✅ Marked as resolved: ${results.database.successful.length}\n`;
        message += `❌ Update failed: ${results.database.failed.length}\n\n`;
      }

      if (results.lambda.failed.length > 0) {
        message += `❌ Failed Lambda Invocations:\n`;
        results.lambda.failed.forEach(f => {
          message += `• ${f.jobId}: ${f.error}\n`;
        });
        message += `\n`;
      }

      if (results.database.failed.length > 0) {
        message += `❌ Failed Database Updates:\n`;
        results.database.failed.forEach(f => {
          message += `• ${f.jobId}: ${f.error}\n`;
        });
      }

      alert(message);
      setSelectedJobIds(new Set());

      setTimeout(async () => {
        try {
          await loadData(true);
        } catch (error) {
          console.error('Error refreshing data:', error);
        }
      }, 1000);

    } catch (error) {
      console.error('Error in retry process:', error);
      alert('Error in retry process: ' + error.message);
    } finally {
      setIsRetrying(false);
    }
  };

  // Handle mark selected jobs as resolved (without retriggering)
  const handleMarkAsResolved = async () => {
    if (selectedJobIds.size === 0) {
      alert('Please select jobs to mark as resolved');
      return;
    }

    const confirmMark = window.confirm(
      `Are you sure you want to mark ${selectedJobIds.size} job(s) as resolved without retriggering them?`
    );

    if (!confirmMark) {
      return;
    }

    setIsMarkingResolved(true);
    try {
      const selectedJobs = data.filter(job => selectedJobIds.has(job.job_id));
      const results = await markJobsAsResolved(selectedJobs);

      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      let message = `✅ Mark as Resolved Completed:\n\n`;
      message += `✅ Successfully marked: ${successful.length} job(s)\n`;
      message += `❌ Failed to mark: ${failed.length} job(s)\n\n`;

      if (failed.length > 0) {
        message += `❌ Failed Updates:\n`;
        failed.forEach(f => {
          message += `• ${f.jobId}: ${f.error}\n`;
        });
      }

      alert(message);
      setSelectedJobIds(new Set());

      // Refresh data after marking
      setTimeout(async () => {
        try {
          await loadData(true);
        } catch (error) {
          console.error('Error refreshing data:', error);
        }
      }, 1000);

    } catch (error) {
      console.error('Error marking jobs as resolved:', error);
      alert('Error marking jobs as resolved: ' + error.message);
    } finally {
      setIsMarkingResolved(false);
    }
  };

  // Handle export selected jobs
  const handleExportJobs = () => {
    if (selectedJobIds.size === 0) {
      alert('Please select jobs to export');
      return;
    }

    const selectedJobs = data.filter(job => selectedJobIds.has(job.job_id));
    const exportData = selectedJobs.map(job => ({
      job_id: job.job_id,
      failure_timestamp: job.failure_timestamp,
      doctor_name: job.doctor_name,
      patient_id: job.patient_id,
      function_name: job.function_name,
      error_message: job.error_message,
      retry_count: job.retry_count,
      is_resolved: job.is_resolved,
      raw_job_name: job.raw_job_name,
      event: job.event,
      environment: job.environment,
      log_stream_name: job.log_stream_name,
      status: job.status,
      history_of_present_illness: job.history_of_present_illness,
      interval_history: job.interval_history,
      assessment: job.assessment,
      plan: job.plan
    }));

    const exportText = JSON.stringify(exportData, null, 2);
    const blob = new Blob([exportText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `failed_jobs_${selectedDate || 'today'}_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Load data function (initial or refresh)
  const loadData = async (resetPagination = true) => {
    setLoading(true);
    setError(null);
    try {
      const result = await queryFailedJobs(selectedDate, resetPagination ? null : lastKey, pageSize, resolvedFilter);

      if (resetPagination) {
        setData(result.items);
        setTotalLoaded(result.items.length);

        if (result.items.length > 0 && !activeTab) {
          const firstFunction = result.items[0].function_name;
          setActiveTab(firstFunction);
        }
      } else {
        setData(prevData => [...prevData, ...result.items]);
        setTotalLoaded(prev => prev + result.items.length);
      }

      setLastKey(result.lastKey);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Failed to load data:', error);
      setError(error.message);
      setData([]);
      setTotalLoaded(0);
    } finally {
      setLoading(false);
    }
  };

  // Load more data (pagination)
  const loadMore = async () => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const result = await queryFailedJobs(selectedDate, lastKey, pageSize, resolvedFilter);
      setData(prevData => [...prevData, ...result.items]);
      setTotalLoaded(prev => prev + result.items.length);
      setLastKey(result.lastKey);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Failed to load more data:', error);
      setError(error.message);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Load data when component mounts, date changes, or filter changes
  useEffect(() => {
    loadData(true);
  }, [selectedDate, resolvedFilter]);

  const functionNames = useMemo(() => {
    const unique = Array.from(new Set(data.map((item) => item.function_name)));
    return ["All", ...unique];
  }, [data]);

  const filteredFunctions = selectedFunctions.includes("All")
    ? functionNames.slice(1)
    : selectedFunctions;

  const filteredData = useMemo(() => {
    return data.filter(
      (job) => filteredFunctions.includes(job.function_name)
    );
  }, [selectedFunctions, data]);

  const activeTabData = useMemo(() => {
    if (!activeTab) return [];
    return filteredData.filter((job) => job.function_name === activeTab);
  }, [filteredData, activeTab]);

  const isAllSelected = activeTabData.length > 0 && activeTabData.every(job => selectedJobIds.has(job.job_id));
  const isSomeSelected = activeTabData.some(job => selectedJobIds.has(job.job_id));

  const columns = useMemo(
    () => [
      {
        header: () => (
          <input
            type="checkbox"
            checked={isAllSelected}
            ref={(el) => {
              if (el) el.indeterminate = isSomeSelected && !isAllSelected;
            }}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="rounded"
          />
        ),
        accessorKey: "select",
        cell: (info) => (
          <input
            type="checkbox"
            checked={selectedJobIds.has(info.row.original.job_id)}
            onChange={(e) => handleJobSelect(info.row.original.job_id, e.target.checked)}
            className="rounded"
          />
        ),
        size: 50,
      },
      {
        header: "Raw Job Name",
        accessorKey: "raw_job_name",
      },
      {
        header: "Doctor",
        accessorKey: "doctor_name",
      },
      {
        header: "Function",
        accessorKey: "function_name",
      },
      {
        header: "Error",
        accessorKey: "error_message",
      },
      {
        header: "Retry Count",
        accessorKey: "retry_count",
      },
      {
        header: "Resolved",
        accessorKey: "is_resolved",
        cell: (info) => (info.getValue() ? "Yes" : "No"),
      },
      {
        header: 'Patient ID',
        accessorKey: "patient_id",
      },
      {
        header: "Date",
        accessorKey: "failure_timestamp",
        cell: (info) => info.getValue().slice(0, 10),
      },
      {
        header: "Actions",
        accessorKey: "actions",
        cell: (info) => {
          const job = info.row.original;

          return (
            <div>
              {job.is_resolved && (
                <button
                  onClick={() => handleViewLogs(job)}
                  className="px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  View Logs
                </button>
              )}
            </div>
          );
        },
        size: 100,
      },
    ],
    [selectedJobIds, isAllSelected, isSomeSelected]
  );

  const table = useReactTable({
    data: activeTabData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="p-6 space-y-6">
      {loading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-sm text-gray-600">Loading failed jobs from DynamoDB...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="text-red-400">⚠</div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination info banner */}
      {totalLoaded > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-blue-700">
              Loaded <span className="font-semibold">{totalLoaded}</span> jobs
              {hasMore && " (more available)"}
            </div>
            {hasMore && (
              <button
                onClick={loadMore}
                disabled={isLoadingMore}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingMore ? 'Loading...' : `Load More (${pageSize})`}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 items-start md:items-end justify-start">
        <div className="flex flex-col space-y-1">
          <label htmlFor="function" className="text-sm font-medium">
            Function
          </label>
          <select
            id="function"
            className="min-w-[200px] border rounded px-2 py-1"
            value={selectedFunctions[0] || "All"}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedFunctions([value]);
              setActiveTab(value === "All" ? functionNames[1] : value);
            }}
            disabled={loading}
          >
            {functionNames.map((fn) => (
              <option key={fn} value={fn}>
                {fn}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col space-y-1">
          <label htmlFor="resolved" className="text-sm font-medium">
            Status
          </label>
          <select
            id="resolved"
            className="min-w-[150px] border rounded px-2 py-1"
            value={resolvedFilter}
            onChange={(e) => setResolvedFilter(e.target.value)}
            disabled={loading}
          >
            <option value="Unresolved">Unresolved</option>
            <option value="Resolved">Resolved</option>
          </select>
        </div>

        <div className="flex flex-col space-y-1">
          <label htmlFor="date" className="text-sm font-medium">
            Date
          </label>
          <input
            id="date"
            type="date"
            className="border rounded px-2 py-1"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            placeholder="Leave empty for today"
            disabled={loading}
          />
        </div>

        <div className="flex flex-col space-y-1">
          <label className="text-sm font-medium text-transparent">Actions</label>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setData([]);
                setError(null);
                setSelectedJobIds(new Set());
                setTotalLoaded(0);
                loadData(true);
              }}
              disabled={loading}
              className="px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>

            {selectedJobIds.size > 0 && (
              <>
                <button
                  onClick={handleRetryJobs}
                  disabled={isRetrying || loading}
                  className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRetrying ? 'Retrying...' : `Retry (${selectedJobIds.size})`}
                </button>

                <button
                  onClick={handleMarkAsResolved}
                  disabled={isMarkingResolved || loading}
                  className="px-3 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isMarkingResolved ? 'Marking...' : `Mark Resolved (${selectedJobIds.size})`}
                </button>

                <button
                  onClick={handleExportJobs}
                  disabled={loading}
                  className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Export ({selectedJobIds.size})
                </button>

                <button
                  onClick={handleDeleteJobs}
                  disabled={isDeleting || loading}
                  className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : `Delete (${selectedJobIds.size})`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {!loading && data.length === 0 && !error && (
        <div className="text-center py-8">
          <p className="text-gray-500">No failed jobs found for the selected date.</p>
          <p className="text-sm text-gray-400 mt-1">Try selecting a different date or check your DynamoDB connection.</p>
        </div>
      )}

      {/* Tab Header */}
      <div className="flex gap-2 border-b">
        {filteredFunctions.map((fn) => (
          <button
            key={fn}
            onClick={() => setActiveTab(fn)}
            className={clsx(
              "px-4 py-2 text-sm border-b-2",
              activeTab === fn
                ? "border-purple-500 font-semibold text-purple-600"
                : "border-transparent text-gray-500 hover:text-black"
            )}
          >
            {fn}
          </button>
        ))}
      </div>

      {/* Tab Body */}
      <div>
        {activeTabData.length > 0 ? (
          <>
            {renderTable(table)}

            {/* Load more button at bottom of table */}
            {hasMore && (
              <div className="mt-4 text-center">
                <button
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingMore ? (
                    <span className="flex items-center gap-2 justify-center">
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading more...
                    </span>
                  ) : (
                    `Load More Jobs (${pageSize})`
                  )}
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500 mt-4">No jobs found.</p>
        )}
      </div>

      {/* Logs Viewer Modal */}
      {logJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-6xl rounded-lg shadow-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex-1 overflow-y-auto p-4">
              <LogsViewer
                jobId={logJob.job_id}
                functionName={logJob.function_name}
                requestId={logJob.retry_request_id}
                doctorName={logJob.doctor_name}
              />
            </div>

            <div className="p-3 border-t flex justify-end">
              <button
                onClick={handleCloseLogs}
                className="px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Close Logs
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FailedJobsTable;